import {useMemo, useState} from 'react';
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {GridColDef} from '@mui/x-data-grid';
import {Apartment} from 'src/apis/autoGenerate/types/apartment';
import {Building} from 'src/apis/autoGenerate/types/building';
import {IntercomDevice} from 'src/apis/autoGenerate/types/intercomDevice';
import {EntioDataGrid, StatusBadge, getDeviceStatusVariant} from 'src/components/ui';
import {getDeviceLocation, isDeviceOnline} from './types';

interface DevicesTabProps {
  devices: IntercomDevice[];
  apartments: Apartment[];
  buildings: Building[];
  propertyId: string;
  buildingId: string;
  onPropertyChange: (propertyId: string) => void;
  onBuildingChange: (buildingId: string) => void;
  integrationByDeviceId: Record<string, string>;
  loading: boolean;
  openingDeviceId?: string;
  onOpenDoor: (device: IntercomDevice) => void;
}

export default function DevicesTab({
  devices,
  apartments,
  buildings,
  propertyId,
  buildingId,
  onPropertyChange,
  onBuildingChange,
  integrationByDeviceId,
  loading,
  openingDeviceId,
  onOpenDoor,
}: DevicesTabProps) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');

  const buildingsForFilter = useMemo(() => {
    if (propertyId === 'all') return buildings;
    return buildings.filter(building => building.apartmentId === propertyId);
  }, [buildings, propertyId]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return devices.filter(device => {
      if (propertyId !== 'all' && (device.apartmentId || device.apartment?.id) !== propertyId) {
        return false;
      }
      if (buildingId !== 'all' && (device.buildingId || device.building?.id) !== buildingId) {
        return false;
      }
      if (status === 'online' && !isDeviceOnline(device)) return false;
      if (status === 'offline' && isDeviceOnline(device)) return false;
      if (!query) return true;
      const haystack = [device.name, device.apartment?.name, device.building?.name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [buildingId, devices, propertyId, search, status]);

  const columns: GridColDef<IntercomDevice>[] = [
    {
      field: 'name',
      headerName: 'Device Name',
      flex: 1.1,
      minWidth: 160,
      valueGetter: params => params.row.name || '—',
    },
    {
      field: 'property',
      headerName: 'Property',
      flex: 1,
      minWidth: 140,
      valueGetter: params => params.row.apartment?.name || '—',
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      minWidth: 140,
      valueGetter: params => getDeviceLocation(params.row),
    },
    {
      field: 'integration',
      headerName: 'Integration',
      flex: 0.7,
      minWidth: 110,
      valueGetter: params =>
        params.row.integration || integrationByDeviceId[params.row.id || ''] || '—',
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.6,
      minWidth: 90,
      renderCell: params => {
        const online = isDeviceOnline(params.row);
        return (
          <StatusBadge
            status={getDeviceStatusVariant(params.row.status)}
            label={online ? 'Online' : 'Offline'}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.7,
      minWidth: 120,
      sortable: false,
      renderCell: params => (
        <Button
          variant="contained"
          size="small"
          disabled={!isDeviceOnline(params.row) || openingDeviceId === params.row.id}
          onClick={() => onOpenDoor(params.row)}
        >
          Open Door
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap'}}>
        <OutlinedInput
          size="small"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder="Search devices..."
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          }
          sx={{flex: 1, minWidth: 220, bgcolor: 'background.paper'}}
        />
        <FormControl size="small" sx={{minWidth: 150}}>
          <Select
            value={propertyId}
            onChange={event => onPropertyChange(event.target.value)}
          >
            <MenuItem value="all">All Properties</MenuItem>
            {apartments.map(apartment => (
              <MenuItem key={apartment.id} value={apartment.id}>
                {apartment.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{minWidth: 150}}>
          <Select value={buildingId} onChange={event => onBuildingChange(event.target.value)}>
            <MenuItem value="all">All Buildings</MenuItem>
            {buildingsForFilter.map(building => (
              <MenuItem key={building.id} value={building.id}>
                {building.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{minWidth: 130}}>
          <Select value={status} onChange={event => setStatus(event.target.value)}>
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="online">Online</MenuItem>
            <MenuItem value="offline">Offline</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <EntioDataGrid
        rows={filtered}
        columns={columns}
        loading={loading}
        getRowId={row => row.id || row.deviceId || row.name || Math.random().toString()}
        emptyMessage="No devices"
        emptyDescription="No accessible entrances match the current filters."
      />
    </Box>
  );
}
